import React from 'react'
import { Box, Stack, InputGroup, InputLeftElement, Icon, Input, HStack, Button, IconButton, useToast, Menu, MenuButton, MenuList, Container, MenuItem } from '@chakra-ui/react'
import { FiMenu, FiRefreshCw, FiSearch } from 'react-icons/fi'
import { ExpertTable } from './ExpertTable'
import { TelemetryClientService } from '../services/TelemetryClientService'
import { IExpert } from '@/shared/IExpert'
import { NewExpertDialog } from './NewExpertDialog'
import { AuthService } from '../services/AuthService'
import { AppContext } from '../comments/AppContext'
import { CourseExpertStorage } from '../services/CourseExpertStorage'
import { StorageService } from '../services/StorageService'
import { stringifyError } from '@/shared/parse'
import { MenuItemOpenSharePoint } from '../course/MenuItemOpenSharePoint'
import { useFilterText } from './filters/useFilterText'
import { EditExpertDialog } from './EditExpertDialog'
import { CheckboxFilterRole } from './filters/CheckboxFilterRole'
import { Divider } from '@chakra-ui/react'
import { useLocalStorageState } from '../components/useLocalStorageState'
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog'
import { AppToolbar } from '../navbar/AppToolbar';
import { FilterBox } from './FilterBox'
import { TableCounterBox } from './TableCounterBox'

export const ExpertTableFrame = (props: {
  errorMessage?: any, setErrorMessage: (error: any) => void,
}) => {

  TelemetryClientService.page('ExpertTableFrame');
  const [isLoading, setIsLoading] = React.useState(true);

  const toast = useToast({
    duration: 3000,
  });

  const onSendExpertInvitation = async (expert: IExpert) => {
    toast.promise(CourseExpertStorage.inviteExpert(expert.id), {
      loading: { title: `Sending invitation to ${expert.email}...` },
      success: { title: 'The invitation has been sent.' },
      error: err => ({ title: 'Unable to send the invitation', description: stringifyError(err) }),
    });
  }

  const { isAuthenticated } = React.useContext(AppContext);

  const loadExperts = (force?: boolean) => StorageService.loadItems(CourseExpertStorage.getExperts, props.setErrorMessage, setIsLoading, force).then(setAllExperts);

  React.useEffect(() => {
    if (AuthService.isStaff) {
      loadExperts(false);
    }
  }, [isAuthenticated]);

  const [filterText, setFilterText, filterWords] = useFilterText('eitum-experts-table-filter-text');
  const [filterRole, setFilterRole] = useLocalStorageState<string[] | undefined>('eitum-experts-table-filter-role', undefined);

  const [allExperts, setAllExperts] = React.useState<IExpert[]>([]);

  const experts = React.useMemo(() => {
    return allExperts.filter(p => {
      // Role filter
      if (filterRole?.length && filterRole.every(role => p.role !== role)) {
        return false;
      }

      // Text filter
      if (filterWords.length > 0) {
        return filterWords.every(w => [p.name, p.email].some(t => t?.toLowerCase().includes(w)))
      }

      return true;
    });
  }, [allExperts, filterWords, filterRole]);

  // Function to clear all filters
  const clearAllFilters = () => {
    setFilterText('');
    setFilterRole(undefined);
  };

  // Check if any filters are active
  const hasActiveFilters = React.useMemo(() => {
    return !!(
      filterText ||
      (filterRole?.length && filterRole.length > 0)
    );
  }, [filterText, filterRole]);

  const [selectedExpert, setSelectedExpert] = React.useState<IExpert>();
  const [expertToDelete, setExpertToDelete] = React.useState<IExpert>();

  const onAddNewExpert = async (expert: IExpert, invite: boolean) => {
    const created = await CourseExpertStorage.createExpert(expert, invite);
    setAllExperts(old => [...old, created]);
  }

  const onShowNewExpertDialog = () => {
    setIsNewExpertDialogOpen(true);
  }

  const onSaveExpert = async (expert: IExpert, invite: boolean) => {
    const updated = await CourseExpertStorage.patchExpert(expert, invite);
    setAllExperts(old => old.map(p => p.id === updated.id ? updated : p));
  }

  const onEditExpert = async (expert: IExpert) => {
    setSelectedExpert({
      id: expert.id,
      name: expert.name,
      email: expert.email,
      role: expert.role,
    })
  }

  const onDeleteExpert = async (expert: IExpert) => {
    setExpertToDelete(expert);
  }

  const onConfirmDeleteExpert = async (expert: IExpert) => {
    await CourseExpertStorage.deleteExpert(expert.id);
    setAllExperts(p => p.filter(c => c.id !== expert.id));
  }

  const [isNewExpertDialogOpen, setIsNewExpertDialogOpen] = React.useState(false);

  return (
    <>
      <AppToolbar>
        <HStack>
          <Button hideBelow='md' variant='ghost' isDisabled={isLoading || !!props.errorMessage} onClick={onShowNewExpertDialog} size="md" >Add New Expert...</Button>
          <IconButton hideBelow='md' onClick={() => loadExperts(true)} aria-label="Refresh" isDisabled={isLoading} variant='ghost' icon={<Icon as={FiRefreshCw} />} />
          <Menu>
            <MenuButton as={IconButton} isDisabled={isLoading || !AuthService.isAdmin} variant='ghost' icon={<FiMenu />}>Actions</MenuButton>
            <MenuList>
              <MenuItem hideFrom='md' isDisabled={isLoading || !!props.errorMessage} onClick={() => loadExperts(true)}>Refresh</MenuItem>
              <MenuItemOpenSharePoint relativeUrl='Lists/Experts' />
              <MenuItem hideFrom='md' isDisabled={isLoading || !!props.errorMessage} onClick={onShowNewExpertDialog}>Add New Provider...</MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </AppToolbar>
      <Box opacity={isLoading ? 0.5 : undefined} maxW='full'>
        <Stack>
          <Container>
            <HStack justify="space-between">
              <Stack direction={{ base: 'column', md: 'row' }} justify="space-between">
                <HStack spacing="3" justify='start'>
                  <InputGroup maxW="xs">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiSearch} color="fg.muted" boxSize="5" />
                    </InputLeftElement>
                    <Input placeholder="Search" value={filterText} onChange={evt => setFilterText(evt.target.value)} />
                  </InputGroup>
                  <Divider hideBelow='md' orientation='vertical' />
                  <FilterBox isDisabled={isLoading} onClearAllFilters={clearAllFilters} hasActiveFilters={hasActiveFilters}>
                    <CheckboxFilterRole allExperts={allExperts} value={filterRole} onSetFilter={setFilterRole} />
                  </FilterBox>
                </HStack>
              </Stack>
              <TableCounterBox isLoading={isLoading} count={experts.length}  label='expert(s)' />
            </HStack>
          </Container>
          <Box overflowX="auto">
            <ExpertTable
              filter={filterWords}
              experts={experts}
              onSendExpertInvitation={onSendExpertInvitation}
              onDeleteExpert={onDeleteExpert}
              onEditExpert={onEditExpert}
            />
          </Box>
        </Stack>
        {isNewExpertDialogOpen && <NewExpertDialog
          onClose={() => setIsNewExpertDialogOpen(false)}
          onAddNewExpert={onAddNewExpert}
        />}
        {selectedExpert && <EditExpertDialog
          expert={selectedExpert}
          setExpert={setSelectedExpert}
          onClose={() => setSelectedExpert(undefined)}
          onSave={onSaveExpert}
        />}
        {expertToDelete && <DeleteConfirmationDialog
          title='Delete Expert'
          description={`The expert "${expertToDelete?.name}" (${expertToDelete?.email}) will be moved to the site recycle bin. Only experts that have no courses assigned can be deleted. Do you want to proceed?`}
          data={expertToDelete}
          onDelete={onConfirmDeleteExpert}
          onClose={() => setExpertToDelete(undefined)}
        />}
      </Box>
    </>
  )
}